import { AsyncPipe } from '@angular/common';
import { Component, inject, input } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  CategoryStoreSelectors,
  DevicesOsUpdateStatusSelectors,
  DevicesOsUpdateStatusStates,
  DevicesRouterStoreSelectors,
  DeviceStoreSelectors,
} from '@era/stores';
import { Store } from '@ngrx/store';
import { environment } from '@rocksoft/environment';
import { Device } from '@rocksoft/shared';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { DeviceInstalledApplicationsStoreSelectors } from '../../sub-features/applications-on-device/device-installed-applications-store';
import { DeviceSyncStatusSelectors } from '../../sub-features/device-sync/device-sync-statuses-store';
import { DeviceGridItemComponent } from './device-grid-item.component';
@Component({
  selector: 'era-device-grid-item',
  templateUrl: './device-grid-item.container.html',
  imports: [DeviceGridItemComponent, AsyncPipe],
})
export class DeviceGridItemContainerComponent {
  private readonly store = inject(Store);

  public readonly device = input.required<Device>();

  public readonly hasWarning$: Observable<boolean>;
  public readonly isSelected$: Observable<boolean>;
  public readonly osUpdateStatus$: Observable<DevicesOsUpdateStatusStates.OSUpdateStatus | undefined>;
  public readonly isApplicationInSync$: Observable<boolean>;
  public readonly isInUse$: Observable<boolean | undefined>;
  public osUpdateStatus: DevicesOsUpdateStatusStates.OSUpdateStatus;

  constructor() {
    const isAppManagementEnabled = environment.features.appmanagement.enabled;
    const device$ = toObservable(this.device);

    this.hasWarning$ = device$.pipe(
      switchMap((x) => this.store.select(DeviceSyncStatusSelectors.selectIsDeviceInCorrectSyncState(x.udid))),
      map((inCorrectState) => !inCorrectState)
    );

    this.isSelected$ = combineLatest([device$, this.store.select(DevicesRouterStoreSelectors.selectUdid)]).pipe(
      map(([device, udid]) => device.udid === udid)
    );

    this.osUpdateStatus$ = device$.pipe(
      switchMap((x) => this.store.select(DevicesOsUpdateStatusSelectors.selectOsUpdateStatusWithUdidParam(x.udid)))
    );

    this.isApplicationInSync$ = device$.pipe(
      switchMap((x) =>
        this.store.select(
          DeviceInstalledApplicationsStoreSelectors.selectConfiguredApplicationsAreInstalledByUdid(x.udid)
        )
      )
    );

    if (!isAppManagementEnabled) {
      this.isInUse$ = of(undefined);
    } else {
      this.isInUse$ = device$.pipe(
        switchMap((device: Device) => {
          if (device.roomUniqueId) {
            return this.store
              .select(CategoryStoreSelectors.selectCategoryByUniqueId('Room', device.roomUniqueId))
              .pipe(map((x) => x?.entity?.isCheckedIn ?? false));
          }
          if (device.kioskUniqueId) {
            return this.store.select(DeviceStoreSelectors.selectDeviceApplicationIsInUseByUdid(device.udid));
          }
          return of(undefined);
        })
      );
    }
  }
}
